import {
  Badge,
  Button,
  Divider,
  FlexBox,
  IconButton,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { SearchConversationEnriched } from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import moment, { unitOfTime } from 'moment-timezone';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { appActions } from 'app/app.actions';
import { getConversationTitle } from 'app/utils/conversationTitle';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationLinkModal } from './components/conversationLinkModal/conversationLinkModal';
import { ConversationTimelineDetailsItem } from './components/conversationTimelineDetailsItem';
import { ConversationTimelineDetailsRequesterFilter } from './components/conversationTimelineDetailsRequesterFilter';
import {
  ConversationTimelineDetailsSortMenu,
  sortOptions,
} from './components/conversationTimelineDetailsSortMenu';
import styles from './conversationTimelineDetails.module.scss';
import { useConversationsStateEffect } from './utils/useConversationsStateEffect';

type ConversationTimelineDetailsProps = {
  csid: number;
  requesterId: string | undefined;
  handleToggleTimeline: () => void;
};

export const ConversationTimelineDetails = memo(
  function ConversationTimelineDetails({
    csid,
    requesterId,
    handleToggleTimeline,
  }: ConversationTimelineDetailsProps): JSX.Element {
    const dispatch = useAppDispatch();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [requesterFilterIds, setRequesterFilterIds] = useState<string[]>([]);
    const [selectedSortOption, setSelectedSortOption] = useState(
      sortOptions[0],
    );

    /**
     * The current value of the requesterId. Used to prevent unnecessary reloads
     * when selecting a different conversation in the timeline.
     *
     * Reason: when a different conversation loads requesterId value becomes
     * undefined until the new conversation is loaded, this current_requester ->
     * undefined -> next_requester change is the reason for reloads.
     */
    const requesterIdArg = useRef<string>();

    const [
      readSearchConversations,
      { results: conversations, isFetching, hasNextPage, fetchNextPage },
    ] = useLazyReadSearchInfiniteQuery<'conversations', true>();

    const { currentlyClosedCsids, currentlyLinkedCsids } =
      useConversationsStateEffect({ requesterId, conversations });

    useEffect(() => {
      setSelectedIds([]);
    }, [csid]);

    useEffect(() => {
      if (!requesterId || requesterIdArg.current === requesterId) {
        return;
      }

      requesterIdArg.current = requesterId;

      setRequesterFilterIds((prev) => {
        // Prevent reload if switched to a conversation with requesterId already selected in a requester dropdown.
        if (prev.includes(requesterId)) {
          return prev;
        }
        return [requesterId];
      });
    }, [requesterId]);

    useEffect(() => {
      if (requesterFilterIds.length === 0) {
        return;
      }

      readSearchConversations({
        scope: 'conversations',
        filters: {
          strategy: 'any',
          filters: requesterFilterIds.map((value) => ({
            field: 'requested_by',
            operator: 'is_one',
            values: [{ value }],
          })),
        },
        sortBy: {
          field: selectedSortOption.key,
          order: selectedSortOption.direction,
        },
        enrichmentSpec: 'ALL',
      });
    }, [requesterFilterIds, selectedSortOption, readSearchConversations]);

    const getTitle = useCallback(
      ({
        initial_direction: direction,
        subject,
        initial_channel,
        requested_by: requester,
      }: SearchConversationEnriched) =>
        getConversationTitle({
          conversation: {
            direction,
            attributes: subject ? { subject } : undefined,
            initial_channel: initial_channel
              ? (convertChannelCasing(
                  initial_channel,
                  'truncate',
                ) as ConversationsChannels)
              : undefined,
          },
          requester: requester,
        }),
      [],
    );

    const onCheck = useCallback(
      (csid: number) => {
        if (selectedIds.includes(csid)) {
          setSelectedIds((ids) => ids.filter((id) => id !== csid));
        } else {
          setSelectedIds((ids) => [...ids, csid]);
        }
      },
      [selectedIds],
    );

    const onClear = useCallback(() => {
      if (requesterId) {
        setRequesterFilterIds([requesterId]);
      }
    }, [requesterId]);

    const handleLink = useCallback(async () => {
      ampli.linkConversationsModalOpened();

      const { isLinkInitiated } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:conversationLink',
            arg: { csids: selectedIds, parent_csid: csid },
          },
        }),
      );

      if (isLinkInitiated) {
        setSelectedIds([]);
      }
    }, [csid, dispatch, selectedIds]);

    const handleUnselectAll = useCallback(() => {
      setSelectedIds([]);
    }, []);

    const renderItemsWithSections = useCallback(() => {
      const groups = [
        { label: 'Today', granularity: 'day', used: false },
        { label: 'This week', granularity: 'week', used: false },
        { label: 'This month', granularity: 'month', used: false },
        { label: 'Earlier', used: false },
      ];
      const now = new Date();

      return conversations.map((conversation) => {
        const group = groups.find(({ granularity }) =>
          granularity
            ? moment(conversation[selectedSortOption.key]).isSame(
                now,
                granularity as unitOfTime.StartOf,
              )
            : true,
        );
        const linkedCsids = currentlyLinkedCsids[conversation.id];
        const timelineItem = (
          <ConversationTimelineDetailsItem
            key={conversation.id}
            conversation={conversation}
            title={getTitle(conversation)}
            isCurrentConversation={conversation.id === csid}
            isChecked={selectedIds.includes(conversation.id)}
            linkedTo={linkedCsids}
            // Conversation status could be changed after loading the conversation list. Like with bulk actions, etc.
            status={currentlyClosedCsids[conversation.id]}
            onCheck={onCheck}
          />
        );
        const renderItem =
          group?.used === false ? (
            <Fragment key={group.label}>
              <Divider className={styles.divider}>
                <Text.Paragraph variant="1" subdued>
                  {group.label}
                </Text.Paragraph>
              </Divider>
              {timelineItem}
            </Fragment>
          ) : (
            timelineItem
          );

        if (group?.used === false) {
          group.used = true;
        }

        return renderItem;
      });
    }, [
      conversations,
      csid,
      currentlyClosedCsids,
      currentlyLinkedCsids,
      getTitle,
      onCheck,
      selectedIds,
      selectedSortOption.key,
    ]);

    return (
      <FlexBox background="neutral.000" flexDirection="column" height="100%">
        <FlexBox
          justifyContent="space-between"
          alignItems="center"
          padding="200 200 200 400"
        >
          <FlexBox justifyContent="flex-start" gap="0 100">
            {selectedIds.length > 0 ? (
              <>
                <Button
                  onClick={handleLink}
                  slotEnd={
                    <Badge variant="secondary" size="small">
                      {/* Parent conversation is always selected, hence N + 1  */}
                      {selectedIds.length + 1}
                    </Badge>
                  }
                >
                  Link
                </Button>

                <IconButton
                  variant="tertiary"
                  onClick={handleUnselectAll}
                  icon="undo"
                />
              </>
            ) : (
              <>
                <ConversationTimelineDetailsSortMenu
                  selectedSortOption={selectedSortOption}
                  onSelectSortOption={setSelectedSortOption}
                />

                <ConversationTimelineDetailsRequesterFilter
                  selectedIds={requesterFilterIds}
                  onChange={setRequesterFilterIds}
                  onClear={onClear}
                />
              </>
            )}
          </FlexBox>

          <IconButton
            variant="tertiary"
            onClick={handleToggleTimeline}
            icon={'angle-double-left'}
          />
        </FlexBox>

        <FlexBox className={styles.itemContainer} flexDirection="column">
          {renderItemsWithSections()}

          {isFetching && (
            <FlexBox
              as="li"
              padding="300"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner size={20} />
            </FlexBox>
          )}

          {!isFetching && !conversations.length && (
            <FlexBox as="li" padding="200" justifyContent="center">
              <Text.Paragraph variant="3" subdued>
                No conversations found.
              </Text.Paragraph>
            </FlexBox>
          )}

          {!isFetching && hasNextPage && <Waypoint onEnter={fetchNextPage} />}
        </FlexBox>

        <ConversationLinkModal />
      </FlexBox>
    );
  },
);
